import BaseStepHandler from './BaseStepHandler';
import { Text, Shadow } from 'fabric';

/**
 * Handler for text step type
 */
export default class TextStepHandler extends BaseStepHandler {
    // Default settings for text styling
    static DEFAULT_SETTINGS = {
        // Outline defaults
        outlineWidth: 1,
        outlineColor: '#000000',
        
        // Shadow defaults
        shadowOffsetX: 3,
        shadowOffsetY: 3,
        shadowBlur: 5,
        shadowColor: 'rgba(0,0,0,0.5)'
    };
    
    /**
     * Initialize text elements
     */
    initialize() {
        super.initialize();
    
        // Get the main element ID from definition
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        // Get element from canvas
        let textElement = this.canvas.getElement(elementId);
        this.elementData = this.canvas.getElementData(elementId);
        
        // Store reference if element exists
        if (textElement) {
            this.elements.set(elementId, textElement);
        }
        
        // Flag for keeping track of forced creation state
        this.forcedCreationActive = false;
        
        // Check if this is a force creation element with no existing element
        if (!textElement && this.definition.forceCreation) {
            this.forcedCreationActive = true;
        }

        // ADDED: Apply initial template settings if available
        if (this.elementData?.data) {
            // Get saved template data
            const templateData = this.elementData.data;
            
            // Create initial step data from template settings
            const initialStepData = {
                text: templateData.text || '',
                font: templateData.fontFamily || 'Arial',
                color: templateData.fill || templateData.color || '#000000',
                
                // Include outline settings
                outlineEnabled: templateData.outlineEnabled || false,
                outlineColor: templateData.outlineColor || TextStepHandler.DEFAULT_SETTINGS.outlineColor,
                outlineWidth: templateData.outlineWidth !== undefined ? 
                              templateData.outlineWidth : 
                              TextStepHandler.DEFAULT_SETTINGS.outlineWidth,
                
                // Include shadow settings
                shadowEnabled: templateData.shadowEnabled || false,
                shadowColor: templateData.shadowColor || TextStepHandler.DEFAULT_SETTINGS.shadowColor,
                shadowOffsetX: templateData.shadowOffsetX !== undefined ? 
                              templateData.shadowOffsetX : 
                              TextStepHandler.DEFAULT_SETTINGS.shadowOffsetX,
                shadowOffsetY: templateData.shadowOffsetY !== undefined ? 
                              templateData.shadowOffsetY : 
                              TextStepHandler.DEFAULT_SETTINGS.shadowOffsetY,
                shadowBlur: templateData.shadowBlur !== undefined ? 
                           templateData.shadowBlur : 
                           TextStepHandler.DEFAULT_SETTINGS.shadowBlur
            };
            
            // Update with initial step data
            this.setStepData(initialStepData, false);
            
            // Apply settings to the element
            this.update(initialStepData);
        }

        return this;
    }

    /**
     * Process template when loading in edit mode
     * Reads color and other properties from template element data
     */
    processTemplate() {
        // ALWAYS check template data first, even if stepData exists
        // This ensures we load colors from edit mode
        const elementId = this.definition.elements?.[0];

        // console.log('🔍 [PROCESS_TEMPLATE] Starting for step:', this.stepId);
        // console.log('🔍 [PROCESS_TEMPLATE] Element ID:', elementId);
        // console.log('🔍 [PROCESS_TEMPLATE] Element data exists:', !!this.elementData?.data);

        if (elementId && this.elementData?.data) {
            const templateData = this.elementData.data;

            // console.log('📊 [PROCESS_TEMPLATE] Template data:', templateData);
            // console.log('🎨 [PROCESS_TEMPLATE] Template fill color:', templateData.fill);
            // console.log('🎨 [PROCESS_TEMPLATE] Template color:', templateData.color);

            // Create initial step data from template
            const initialStepData = {
                text: templateData.text || '',
                font: templateData.fontFamily || 'Arial',
                color: templateData.fill || templateData.color || '#000000',

                // Include outline settings
                outlineEnabled: templateData.outlineEnabled || false,
                outlineColor: templateData.outlineColor || TextStepHandler.DEFAULT_SETTINGS.outlineColor,
                outlineWidth: templateData.outlineWidth !== undefined ?
                              templateData.outlineWidth :
                              TextStepHandler.DEFAULT_SETTINGS.outlineWidth,

                // Include shadow settings
                shadowEnabled: templateData.shadowEnabled || false,
                shadowColor: templateData.shadowColor || TextStepHandler.DEFAULT_SETTINGS.shadowColor,
                shadowOffsetX: templateData.shadowOffsetX !== undefined ?
                              templateData.shadowOffsetX :
                              TextStepHandler.DEFAULT_SETTINGS.shadowOffsetX,
                shadowOffsetY: templateData.shadowOffsetY !== undefined ?
                              templateData.shadowOffsetY :
                              TextStepHandler.DEFAULT_SETTINGS.shadowOffsetY,
                shadowBlur: templateData.shadowBlur !== undefined ?
                           templateData.shadowBlur :
                           TextStepHandler.DEFAULT_SETTINGS.shadowBlur
            };

            // console.log('✅ [PROCESS_TEMPLATE] Initial step data created:', initialStepData);
            // console.log('🎨 [PROCESS_TEMPLATE] Color set to:', initialStepData.color);

            this.setStepData(initialStepData, false);

            // Verify what was actually saved
            const savedStepData = this.getStepData();
            // console.log('💾 [PROCESS_TEMPLATE] Step data after setStepData:', savedStepData);
            // console.log('🎨 [PROCESS_TEMPLATE] Saved color value:', savedStepData?.color);

            this.update(initialStepData);

            // Update color picker if panel exists
            if (this.panelElement) {
                const colorPicker = this.panelElement.querySelector('.stc-color-picker');
                if (colorPicker && initialStepData.color) {
                    colorPicker.value = initialStepData.color;
                    // console.log('🖍️ [PROCESS_TEMPLATE] Updated color picker to:', initialStepData.color);
                }
            } else {
                // console.log('⚠️ [PROCESS_TEMPLATE] Panel element does not exist yet');
            }

            return this;
        }

        // console.log('⬅️ [PROCESS_TEMPLATE] Falling back to parent processTemplate()');
        // Fallback to parent implementation if no element data
        return super.processTemplate();
    }

    /**
     * Update text element with step data
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);
        
        // Get main element
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;

        if (this.definition.forceCreation && stepData.text && stepData.text.trim() !== '') {
            if (this.forcedCreationActive) {
                this.createTextElement(elementId, stepData);
                this.forcedCreationActive = false;
            }
        }
        
        const element = this.elements.get(elementId);
        if (!element) return this;
        
        // Update basic properties based on step data
        if (stepData.text !== undefined) element.set('text', stepData.text);
        if (stepData.font !== undefined) element.set('fontFamily', stepData.font);
        if (stepData.color !== undefined) element.set('fill', stepData.color);
        if (stepData.scaleX !== undefined) element.set('scaleX', stepData.scaleX);
        if (stepData.scaleY !== undefined) element.set('scaleY', stepData.scaleY);
        if (stepData.left !== undefined) element.set('left', stepData.left);
        if (stepData.top !== undefined) element.set('top', stepData.top);
        
        // Update outline properties
        if (stepData.outlineEnabled) {
            element.set({
                stroke: stepData.outlineColor || TextStepHandler.DEFAULT_SETTINGS.outlineColor,
                strokeWidth: stepData.outlineWidth || TextStepHandler.DEFAULT_SETTINGS.outlineWidth
            });
        } else {
            element.set({
                stroke: null,
                strokeWidth: 0
            });
        }
        
        // Update shadow properties
        if (stepData.shadowEnabled) {
            const shadowColor = stepData.shadowColor || TextStepHandler.DEFAULT_SETTINGS.shadowColor;
            const offsetX = stepData.shadowOffsetX !== undefined ? 
                            stepData.shadowOffsetX : 
                            TextStepHandler.DEFAULT_SETTINGS.shadowOffsetX;
            const offsetY = stepData.shadowOffsetY !== undefined ? 
                            stepData.shadowOffsetY : 
                            TextStepHandler.DEFAULT_SETTINGS.shadowOffsetY;
            const blur = stepData.shadowBlur !== undefined ? 
                        stepData.shadowBlur : 
                        TextStepHandler.DEFAULT_SETTINGS.shadowBlur;
            
            element.set('shadow', new Shadow({
                color: shadowColor,
                offsetX: offsetX,
                offsetY: offsetY,
                blur: blur
            }));
        } else {
            element.set('shadow', null);
        }
        
        if (this.definition.forceCreation && (!stepData.text || stepData.text.trim() === '')) {
            this.removeTextElement(elementId);
            return this;
        }

        this.canvas.canvas.requestRenderAll();
        
        return this;
    }

    createTextElement(elementId, stepData) {
        
        // Create new text object with default properties
        const textObj = new Text(stepData.text || '', {
            fontFamily: stepData.font || 'Arial',
            fill: stepData.color || '#000000',
            strokeWidth: 0,
            fontSize: 80,
            left: this.canvas.canvas.width / 2,
            top: this.canvas.canvas.height / 2,
            originX: 'center',
            originY: 'center',
            selectable: true,
            hasControls: true,
            lockMovementX: true,
            lockMovementY: true,
            name: elementId
        });
        
        this.canvas.canvas.add(textObj);
        
        this.canvas.canvas.setActiveObject(textObj);

        // Register with STCCanvas tracking
        this.canvas.elementRefs.set(elementId, textObj);
        
        // Add to side elements collection
        if (this.canvas.currentSide === 'front') {
            this.canvas.frontElements.add(elementId);
        } else {
            this.canvas.backElements.add(elementId);
        }
        
        // Store in our handler's elements
        this.elements.set(elementId, textObj);

        this.activate();
        
        return textObj;
    }

    removeTextElement(elementId) {
        // Get the element
        const element = this.elements.get(elementId);
        if (!element) return;
        
        // Remove from canvas
        this.canvas.canvas.remove(element);
        
        // Remove from canvas tracking
        this.canvas.elementRefs.delete(elementId);
        
        // Remove from side elements collections
        this.canvas.frontElements.delete(elementId);
        this.canvas.backElements.delete(elementId);
        
        // Remove from our handler's elements
        this.elements.delete(elementId);
        
        // Reset forced creation state
        this.forcedCreationActive = true;
    }
        
    /**
     * Get available fonts for the text editor
     * @returns {Array} Array of font objects with value and label
     */
    getAvailableFonts() {
        return [
            { value: 'Arial', label: 'Arial' },
            { value: 'Times New Roman', label: 'Times New Roman' },
            { value: 'Helvetica', label: 'Helvetica' },
            { value: 'AgencyFB-Bold', label: 'Agency FB Bold' },
            { value: 'AgencyFB-Reg', label: 'Agency FB Regular' },
            { value: 'Anton', label: 'Anton' },
            { value: 'ArchivoNarrow-Regular', label: 'Archivo Narrow Regular' },
            { value: 'EdwardianScriptITC', label: 'Edwardian Script ITC' },
            { value: 'Freshman', label: 'Freshman' },
            { value: 'GOODBRUSH', label: 'GOODBRUSH' },
            { value: 'Impact', label: 'Impact' },
            { value: 'KristenITC-Regular', label: 'Kristen ITC Regular' },
            { value: 'MyriadPro-BoldCondIt', label: 'Myriad Pro Bold Cond It' },
            { value: 'MyriadPro-Regular', label: 'Myriad Pro Regular' },
            { value: 'OrdinaryGuyDEMO', label: 'Ordinary Guy DEMO' },
            { value: 'OswaldStencil-Bold', label: 'Oswald Stencil Bold' }
        ];
    }
    
    /**
     * Get font select options HTML with styled options
     * @param {string} selectedFont - Currently selected font
     * @returns {string} HTML for select options
     */
    getFontOptionsHtml(selectedFont) {
        const fonts = this.getAvailableFonts();
        
        return fonts.map(font => `
            <option 
                value="${font.value}" 
                ${selectedFont === font.value ? 'selected' : ''} 
                style="font-family: '${font.value}', Arial, sans-serif;">
                ${font.label}
            </option>
        `).join('');
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        const stepData = this.getStepData() || {};
        const selectedFont = stepData.font || this.elementData?.data.fontFamily || 'Arial';

        console.log('🎨 [RENDER_PANEL] Step:', this.stepId);
        console.log('🎨 [RENDER_PANEL] StepData:', stepData);
        console.log('🎨 [RENDER_PANEL] stepData.color:', stepData.color);
        console.log('🎨 [RENDER_PANEL] elementData.fill:', this.elementData?.data?.fill);
        console.log('🎨 [RENDER_PANEL] elementData.color:', this.elementData?.data?.color);

        // 🎯 DEBUG: Log input values being loaded
        const inputValues = {
            text: stepData.text || '',
            color: stepData.color || this.elementData?.data.fill || this.elementData?.data.color || '#000000',
            font: selectedFont,
            stepData: stepData,
            elementData: this.elementData?.data
        };
        console.log('🎨 [RENDER_PANEL] Final color value:', inputValues.color);
        this.sendToWordPressDebugLog('TEXT_INPUT_VALUES_LOADED', inputValues);
        
        // Get advanced option values with defaults
        const outlineEnabled = stepData.outlineEnabled || false;
        const outlineColor = stepData.outlineColor || TextStepHandler.DEFAULT_SETTINGS.outlineColor;
        const outlineWidth = stepData.outlineWidth !== undefined ? 
                            stepData.outlineWidth : 
                            TextStepHandler.DEFAULT_SETTINGS.outlineWidth;
        
        const shadowEnabled = stepData.shadowEnabled || false;
        const shadowColor = stepData.shadowColor || TextStepHandler.DEFAULT_SETTINGS.shadowColor;
        const shadowOffsetX = stepData.shadowOffsetX !== undefined ? 
                            stepData.shadowOffsetX : 
                            TextStepHandler.DEFAULT_SETTINGS.shadowOffsetX;
        const shadowOffsetY = stepData.shadowOffsetY !== undefined ? 
                            stepData.shadowOffsetY : 
                            TextStepHandler.DEFAULT_SETTINGS.shadowOffsetY;
        const shadowBlur = stepData.shadowBlur !== undefined ? 
                        stepData.shadowBlur : 
                        TextStepHandler.DEFAULT_SETTINGS.shadowBlur;
        
        // Check if advanced options section is expanded
        const advancedExpanded = stepData.advancedExpanded || false;

        return `
            <div class="stc-text-field">
                <input type="text" 
                       class="stc-input" 
                       placeholder="Enter your ${this.definition.title.toLowerCase()}"
                       value="${stepData.text || ''}"
                       ${this.definition.required ? 'required' : ''}>
                <div class="stc-text-controls">
                    <input type="color"
                           class="stc-color-picker"
                           value="${stepData.color || this.elementData?.data.fill || this.elementData?.data.color || '#000000'}">
                    <select class="stc-font-select">
                        ${this.getFontOptionsHtml(selectedFont)}
                    </select>
                </div>
                
                <!-- Advanced Options Toggle -->
                <div class="stc-advanced-toggle">
                    <button type="button" class="stc-toggle-advanced">
                        ${advancedExpanded ? 'Hide' : 'Show'} Advanced Options
                        <span class="stc-toggle-icon">${advancedExpanded ? '▲' : '▼'}</span>
                    </button>
                </div>
                
                <!-- Advanced Options Panel -->
                <div class="stc-advanced-options ${advancedExpanded ? 'is-expanded' : ''}">
                    <!-- Outline Controls -->
                    <div class="stc-option-section">
                        <div class="stc-option-header">
                            <label class="stc-switch">
                                <input type="checkbox" class="stc-outline-toggle" ${outlineEnabled ? 'checked' : ''}>
                                <span class="stc-slider"></span>
                            </label>
                            <span class="stc-toggle-label">Text Outline</span>
                        </div>
                        
                        <div class="stc-option-controls ${outlineEnabled ? 'is-enabled' : ''}">
                            <div class="stc-outline-controls">
                                <div class="stc-control-row">
                                    <div class="stc-control-column">
                                        <label>Color</label>
                                        <input type="color" class="stc-outline-color" value="${outlineColor}">
                                    </div>
                                    <div class="stc-control-column">
                                        <label>Width</label>
                                        <div class="stc-slider-container">
                                            <input type="range" 
                                                   class="stc-outline-width" 
                                                   min="0.5" 
                                                   max="5" 
                                                   step="0.5" 
                                                   value="${outlineWidth}">
                                            <span class="stc-slider-value">${outlineWidth}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Shadow Controls -->
                    <div class="stc-option-section">
                        <div class="stc-option-header">
                            <label class="stc-switch">
                                <input type="checkbox" class="stc-shadow-toggle" ${shadowEnabled ? 'checked' : ''}>
                                <span class="stc-slider"></span>
                            </label>
                            <span class="stc-toggle-label">Text Shadow</span>
                        </div>
                        
                        <div class="stc-option-controls ${shadowEnabled ? 'is-enabled' : ''}">
                            <div class="stc-shadow-controls">
                                <div class="stc-control-row">
                                    <div class="stc-control-column">
                                        <label>Color</label>
                                        <input type="color" class="stc-shadow-color" value="${shadowColor.startsWith('rgba') ? '#000000' : shadowColor}">
                                    </div>
                                    <div class="stc-control-column">
                                        <label>Opacity</label>
                                        <div class="stc-slider-container">
                                            <input type="range" 
                                                   class="stc-shadow-opacity" 
                                                   min="0" 
                                                   max="1" 
                                                   step="0.1" 
                                                   value="${this.extractOpacity(shadowColor)}">
                                            <span class="stc-slider-value">${this.extractOpacity(shadowColor)}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="stc-control-row">
                                    <div class="stc-control-column">
                                        <label>X Offset</label>
                                        <div class="stc-slider-container">
                                            <input type="range" 
                                                   class="stc-shadow-offsetx" 
                                                   min="-20" 
                                                   max="20" 
                                                   step="1" 
                                                   value="${shadowOffsetX}">
                                            <span class="stc-slider-value">${shadowOffsetX}</span>
                                        </div>
                                    </div>
                                    <div class="stc-control-column">
                                        <label>Y Offset</label>
                                        <div class="stc-slider-container">
                                            <input type="range" 
                                                   class="stc-shadow-offsety" 
                                                   min="-20" 
                                                   max="20" 
                                                   step="1" 
                                                   value="${shadowOffsetY}">
                                            <span class="stc-slider-value">${shadowOffsetY}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="stc-control-row">
                                    <div class="stc-control-column">
                                        <label>Blur</label>
                                        <div class="stc-slider-container">
                                            <input type="range" 
                                                   class="stc-shadow-blur" 
                                                   min="0" 
                                                   max="20" 
                                                   step="1" 
                                                   value="${shadowBlur}">
                                            <span class="stc-slider-value">${shadowBlur}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        `;
    }
    
    /**
     * Extract opacity value from rgba color string
     * @param {string} color - Color value
     * @returns {number} Opacity value (0-1)
     */
    extractOpacity(color) {
        if (color.startsWith('rgba')) {
            const match = color.match(/rgba\(.*,\s*([0-9.]+)\)/);
            if (match && match[1]) {
                return parseFloat(match[1]);
            }
        }
        return 0.5; // Default opacity
    }
    
    /**
     * Create rgba color from hex color and opacity
     * @param {string} hexColor - Hex color value
     * @param {number} opacity - Opacity value (0-1)
     * @returns {string} RGBA color string
     */
    createRgbaColor(hexColor, opacity) {
        // Convert hex to rgb
        let hex = hexColor.replace('#', '');
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        
        // Return rgba
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements - Basic controls
        const textInput = this.panelElement.querySelector('.stc-input');
        const colorPicker = this.panelElement.querySelector('.stc-color-picker');
        const fontSelect = this.panelElement.querySelector('.stc-font-select');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        
        // Advanced options toggle
        const advancedToggle = this.panelElement.querySelector('.stc-toggle-advanced');
        const advancedPanel = this.panelElement.querySelector('.stc-advanced-options');
        
        // Outline controls
        const outlineToggle = this.panelElement.querySelector('.stc-outline-toggle');
        const outlineColor = this.panelElement.querySelector('.stc-outline-color');
        const outlineWidth = this.panelElement.querySelector('.stc-outline-width');
        const outlineWidthValue = outlineWidth?.nextElementSibling;
        const outlineControls = this.panelElement.querySelector('.stc-outline-controls').closest('.stc-option-controls');
        
        // Shadow controls
        const shadowToggle = this.panelElement.querySelector('.stc-shadow-toggle');
        const shadowColor = this.panelElement.querySelector('.stc-shadow-color');
        const shadowOpacity = this.panelElement.querySelector('.stc-shadow-opacity');
        const shadowOpacityValue = shadowOpacity?.nextElementSibling;
        const shadowOffsetX = this.panelElement.querySelector('.stc-shadow-offsetx');
        const shadowOffsetXValue = shadowOffsetX?.nextElementSibling;
        const shadowOffsetY = this.panelElement.querySelector('.stc-shadow-offsety');
        const shadowOffsetYValue = shadowOffsetY?.nextElementSibling;
        const shadowBlur = this.panelElement.querySelector('.stc-shadow-blur');
        const shadowBlurValue = shadowBlur?.nextElementSibling;
        const shadowControls = this.panelElement.querySelector('.stc-shadow-controls').closest('.stc-option-controls');
        
        // Bind text input event
        this.addEventListener(textInput, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.text = textInput.value;

            // 🎯 DEBUG: Log text input change
            const textChangeData = {
                newText: textInput.value,
                stepData: stepData
            };

            this.setStepData(stepData);
            
            // Enable/disable clear button
            if (clearButton) {
                clearButton.disabled = !textInput.value;
            }
        });
        
        // Bind color picker event
        this.addEventListener(colorPicker, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.color = colorPicker.value;

            // 🎯 DEBUG: Log color picker change
            const colorChangeData = {
                newColor: colorPicker.value,
                stepData: stepData
            };

            this.setStepData(stepData);
        });
        
        // Bind font select event
        this.addEventListener(fontSelect, 'change', () => {
            const stepData = this.getStepData() || {};
            stepData.font = fontSelect.value;

            // 🎯 DEBUG: Log font select change
            const fontChangeData = {
                newFont: fontSelect.value,
                stepData: stepData
            };
            this.sendToWordPressDebugLog('FONT_SELECT_CHANGED', fontChangeData);

            this.setStepData(stepData);
        });
        
        // Bind advanced options toggle
        this.addEventListener(advancedToggle, 'click', () => {
            const stepData = this.getStepData() || {};
            stepData.advancedExpanded = !stepData.advancedExpanded;
            
            // Update UI
            advancedPanel.classList.toggle('is-expanded', stepData.advancedExpanded);
            advancedToggle.querySelector('.stc-toggle-icon').textContent = 
                stepData.advancedExpanded ? '▲' : '▼';
            advancedToggle.firstChild.textContent = 
                stepData.advancedExpanded ? 'Hide ' : 'Show ';
            
            this.setStepData(stepData, false); // Don't trigger another update
        });
        
        // Bind outline toggle
        this.addEventListener(outlineToggle, 'change', () => {
            const stepData = this.getStepData() || {};
            stepData.outlineEnabled = outlineToggle.checked;
            
            // Update UI
            outlineControls.classList.toggle('is-enabled', stepData.outlineEnabled);
            
            this.setStepData(stepData);
        });
        
        // Bind outline color
        this.addEventListener(outlineColor, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.outlineColor = outlineColor.value;
            this.setStepData(stepData);
        });
        
        // Bind outline width
        this.addEventListener(outlineWidth, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.outlineWidth = parseFloat(outlineWidth.value);
            
            // Update value display
            if (outlineWidthValue) {
                outlineWidthValue.textContent = outlineWidth.value;
            }
            
            this.setStepData(stepData);
        });
        
        // Bind shadow toggle
        this.addEventListener(shadowToggle, 'change', () => {
            const stepData = this.getStepData() || {};
            stepData.shadowEnabled = shadowToggle.checked;
            
            // Update UI
            shadowControls.classList.toggle('is-enabled', stepData.shadowEnabled);
            
            this.setStepData(stepData);
        });
        
        // Bind shadow color
        this.addEventListener(shadowColor, 'input', () => {
            const stepData = this.getStepData() || {};
            const opacity = shadowOpacity ? parseFloat(shadowOpacity.value) : 0.5;
            stepData.shadowColor = this.createRgbaColor(shadowColor.value, opacity);
            this.setStepData(stepData);
        });
        
        // Bind shadow opacity
        this.addEventListener(shadowOpacity, 'input', () => {
            const stepData = this.getStepData() || {};
            const hexColor = shadowColor ? shadowColor.value : '#000000';
            const opacity = parseFloat(shadowOpacity.value);
            stepData.shadowColor = this.createRgbaColor(hexColor, opacity);
            
            // Update value display
            if (shadowOpacityValue) {
                shadowOpacityValue.textContent = opacity.toFixed(1);
            }
            
            this.setStepData(stepData);
        });
        
        // Bind shadow offset X
        this.addEventListener(shadowOffsetX, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.shadowOffsetX = parseInt(shadowOffsetX.value);
            
            // Update value display
            if (shadowOffsetXValue) {
                shadowOffsetXValue.textContent = shadowOffsetX.value;
            }
            
            this.setStepData(stepData);
        });
        
        // Bind shadow offset Y
        this.addEventListener(shadowOffsetY, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.shadowOffsetY = parseInt(shadowOffsetY.value);
            
            // Update value display
            if (shadowOffsetYValue) {
                shadowOffsetYValue.textContent = shadowOffsetY.value;
            }
            
            this.setStepData(stepData);
        });
        
        // Bind shadow blur
        this.addEventListener(shadowBlur, 'input', () => {
            const stepData = this.getStepData() || {};
            stepData.shadowBlur = parseInt(shadowBlur.value);
            
            // Update value display
            if (shadowBlurValue) {
                shadowBlurValue.textContent = shadowBlur.value;
            }
            
            this.setStepData(stepData);
        });
        
        // Bind clear button event
        this.addEventListener(clearButton, 'click', () => {
            this.clear();
        });
    }

    /**
     * Get export data for text step
     * @returns {object} Export data
     */
    getStepExportData() {
        const baseData = super.getStepExportData();
        const stepData = this.getStepData() || {};

        return {
            ...baseData,
            content: {
                text: stepData.text || "",
                font: stepData.font || "Arial",
                color: stepData.color || "#000000",
                // Add outline properties
                outlineEnabled: stepData.outlineEnabled || false,
                outlineColor: stepData.outlineColor || TextStepHandler.DEFAULT_SETTINGS.outlineColor,
                outlineWidth: stepData.outlineWidth !== undefined ?
                              stepData.outlineWidth :
                              TextStepHandler.DEFAULT_SETTINGS.outlineWidth,
                // Add shadow properties
                shadowEnabled: stepData.shadowEnabled || false,
                shadowColor: stepData.shadowColor || TextStepHandler.DEFAULT_SETTINGS.shadowColor,
                shadowOffsetX: stepData.shadowOffsetX !== undefined ?
                              stepData.shadowOffsetX :
                              TextStepHandler.DEFAULT_SETTINGS.shadowOffsetX,
                shadowOffsetY: stepData.shadowOffsetY !== undefined ?
                              stepData.shadowOffsetY :
                              TextStepHandler.DEFAULT_SETTINGS.shadowOffsetY,
                shadowBlur: stepData.shadowBlur !== undefined ?
                            stepData.shadowBlur :
                            TextStepHandler.DEFAULT_SETTINGS.shadowBlur
            }
        };
    }

    /**
     * Override to provide text-specific template data properties
     * Maps step handler properties to template format
     * @param {object} stepData - Current step data
     * @param {object} element - Canvas element
     * @returns {object} Template data properties for text elements
     */
    getTemplateDataProperties(stepData, element) {
        // Get base properties
        const baseData = super.getTemplateDataProperties(stepData, element);

        // Add text-specific properties in template format
        return {
            ...baseData,
            // Text content
            text: stepData.text || element.text || "",

            // Font properties (map to template format)
            fontSize: element.fontSize || 80,
            fontFamily: stepData.font || element.fontFamily || "Arial",
            fill: stepData.color || element.fill || "#000000",

            // Outline properties
            outlineEnabled: stepData.outlineEnabled || false,
            outlineColor: stepData.outlineColor || TextStepHandler.DEFAULT_SETTINGS.outlineColor,
            outlineWidth: stepData.outlineWidth !== undefined ?
                          stepData.outlineWidth :
                          TextStepHandler.DEFAULT_SETTINGS.outlineWidth,

            // Shadow properties
            shadowEnabled: stepData.shadowEnabled || false,
            shadowColor: stepData.shadowColor || TextStepHandler.DEFAULT_SETTINGS.shadowColor,
            shadowOffsetX: stepData.shadowOffsetX !== undefined ?
                          stepData.shadowOffsetX :
                          TextStepHandler.DEFAULT_SETTINGS.shadowOffsetX,
            shadowOffsetY: stepData.shadowOffsetY !== undefined ?
                          stepData.shadowOffsetY :
                          TextStepHandler.DEFAULT_SETTINGS.shadowOffsetY,
            shadowBlur: stepData.shadowBlur !== undefined ?
                       stepData.shadowBlur :
                       TextStepHandler.DEFAULT_SETTINGS.shadowBlur,

            // Additional text properties from element
            stroke: element.stroke,
            strokeWidth: element.strokeWidth,
            textAlign: element.textAlign,
            fontWeight: element.fontWeight,
            fontStyle: element.fontStyle,
            underline: element.underline,
            linethrough: element.linethrough,
            shadow: element.shadow
        };
    }
    
    /**
     * Step-specific reset logic
     */
    onReset() {
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Reset text to empty
        element.set('text', '');
        
        // Reset appearance
        element.set({
            fontFamily: 'Arial',
            fill: this.elementData?.data.color || '#000000',
            stroke: null,
            strokeWidth: 0,
            shadow: null,
            visible: this.definition.required !== false
        });
    }

    isComplete() {
        const stepData = this.getStepData() || {};
        
        // For forceCreation elements, they're complete if they have text
        if (this.definition.forceCreation) {
            return stepData.text && stepData.text.trim() !== '';
        }
        
        // Original completion logic for standard elements
        return !(!stepData.text) && 
            (this.elementData?.data?.text != stepData.text);
    }

    clear() {
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        // Handle differently for forceCreation elements
        if (this.definition.forceCreation) {
            // Remove the element entirely if it exists
            if (this.elements.has(elementId)) {
                this.removeTextElement(elementId);
            }
            
            // Clear step data
            this.setStepData({text: ''});
        } else {
            // Use standard reset for normal elements
            this.resetToDefault();
        }
        
        // Update panel UI
        this.updatePanel();
    }

    /**
     * Send debug data to WordPress debug.log for server-side debugging
     *
     * @param {string} label - Label to identify the data type
     * @param {object} data - Data to log
     */
    async sendToWordPressDebugLog(label, data) {
        try {
            // Check if required variables exist
            if (!window.octStcCustomizer?.ajax_url || !window.octStcCustomizer?.nonce) {
                return;
            }

            const response = await fetch(window.octStcCustomizer.ajax_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'write_design_debug',
                    design_data: `[${label}] ${JSON.stringify(data, null, 2)}`,
                    nonce: window.octStcCustomizer.nonce
                })
            });

            const result = await response.json();
            if (result.success) {
            }
        } catch (error) {
        }
    }

}